import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle, Globe, Plug, Shield, Users, Monitor, Smartphone, Tablet } from "lucide-react";

export default function TechRequirements() {
  const [testResults, setTestResults] = useState<Record<string, 'success' | 'warning' | 'error'>>({});

  const runCompatibilityTest = (category: string) => {
    // Simulate compatibility testing
    setTimeout(() => {
      setTestResults(prev => ({ ...prev, [category]: 'success' }));
    }, 1000);
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Technical Requirements</h1>
        <p className="text-muted-foreground">System compatibility and integration capabilities</p>
      </div>

      <Tabs defaultValue="browser-compatibility" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="browser-compatibility">Browser Support</TabsTrigger>
          <TabsTrigger value="api-integrations">API Integrations</TabsTrigger>
          <TabsTrigger value="directory-services">Directory Services</TabsTrigger>
          <TabsTrigger value="sis-integration">SIS Integration</TabsTrigger>
        </TabsList>

        <TabsContent value="browser-compatibility" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Browser & Device Compatibility
              </CardTitle>
              <CardDescription>
                Modern web standards support across all platforms
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Monitor className="h-4 w-4" />
                      Desktop Browsers
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Chrome 90+</span>
                      <Badge variant="secondary">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Supported
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Firefox 88+</span>
                      <Badge variant="secondary">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Supported
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Safari 14+</span>
                      <Badge variant="secondary">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Supported
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Edge 90+</span>
                      <Badge variant="secondary">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Supported
                      </Badge>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Smartphone className="h-4 w-4" />
                      Mobile Browsers
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">iOS Safari 14+</span>
                      <Badge variant="secondary">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Supported
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Android Chrome 90+</span>
                      <Badge variant="secondary">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Supported
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Samsung Internet 14+</span>
                      <Badge variant="secondary">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Supported
                      </Badge>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Tablet className="h-4 w-4" />
                      Tablet Support
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">iPad Safari 14+</span>
                      <Badge variant="secondary">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Supported
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Android Tablets</span>
                      <Badge variant="secondary">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Supported
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Surface Tablets</span>
                      <Badge variant="secondary">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Supported
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  Built with modern web standards (ES2020, CSS Grid, Flexbox) and progressive enhancement for optimal performance across all devices.
                </AlertDescription>
              </Alert>

              <Button 
                onClick={() => runCompatibilityTest('browser')}
                disabled={testResults.browser === 'success'}
              >
                {testResults.browser === 'success' ? 'Tests Passed' : 'Run Compatibility Tests'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="api-integrations" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plug className="h-5 w-5" />
                Third-Party API Integration Capabilities
              </CardTitle>
              <CardDescription>
                RESTful API support for LMS and external systems
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Canvas LMS Integration</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span className="text-sm">Canvas API v1 Support</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span className="text-sm">Course Enrollment Sync</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span className="text-sm">Gradebook Integration</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span className="text-sm">Certificate Posting</span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Generic LTI Support</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span className="text-sm">LTI 1.3 Compliance</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span className="text-sm">Deep Linking Support</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span className="text-sm">Grade Passback</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span className="text-sm">Single Sign-On</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Alert>
                <AlertDescription>
                  REST API endpoints available for custom integrations with webhook support for real-time data synchronization.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="directory-services" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Active Directory & LDAP Compliance
              </CardTitle>
              <CardDescription>
                Enterprise directory service integration
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Microsoft Active Directory</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span className="text-sm">SAML 2.0 Integration</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span className="text-sm">Azure AD Support</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span className="text-sm">Multi-Factor Authentication</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span className="text-sm">Group-Based Role Assignment</span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">LDAP Directory Services</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span className="text-sm">LDAP v3 Protocol</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span className="text-sm">Secure LDAP (LDAPS)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span className="text-sm">Attribute Mapping</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span className="text-sm">Nested Group Support</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Button variant="outline" onClick={() => window.location.href = '/sso-configuration'}>
                Configure SSO Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sis-integration" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Student Information System Integration
              </CardTitle>
              <CardDescription>
                Automated user profile import and synchronization
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Synergy SIS Integration</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span className="text-sm">Staff Directory Sync</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span className="text-sm">Department Mapping</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span className="text-sm">Position Title Import</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span className="text-sm">Automated Role Assignment</span>
                    </div>
                    <Badge variant="outline" className="mt-2">Ready for Configuration</Badge>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">iVisions Portal Integration</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span className="text-sm">Employee Access Portal</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span className="text-sm">Profile Data Sync</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span className="text-sm">Credential Mapping</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span className="text-sm">Bulk Import Support</span>
                    </div>
                    <Badge variant="outline" className="mt-2">Ready for Configuration</Badge>
                  </CardContent>
                </Card>
              </div>

              <Alert>
                <AlertDescription>
                  SIS integrations support both real-time synchronization and scheduled batch imports for maintaining up-to-date user profiles and organizational structures.
                </AlertDescription>
              </Alert>

              <div className="flex gap-2">
                <Button onClick={() => window.location.href = '/sis-configuration'}>
                  Configure SIS Integration
                </Button>
                <Button variant="outline">
                  View Integration Logs
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}